import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import reportService from '../../../services/reportService';
import { getProjects } from '../../../services/projectsService';
import { getMilestones } from '../../../services/milestonesService';
import continuousServiceService from '../../../services/continuousServiceService';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import Toast from '../../../components/Common/Toast';

const Results = () => {
  const { activosEnabled } = useModuleSettings();
  const [mode, setMode] = useState('by_period'); // by_period | by_milestones
  const now = new Date();
  const currentYear = now.getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const [monthFrom, setMonthFrom] = useState('');
  const [yearFrom, setYearFrom] = useState(currentYear);
  const [monthTo, setMonthTo] = useState('');
  const [yearTo, setYearTo] = useState(currentYear);
  const [selectedProject, setSelectedProject] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestoneIds, setSelectedMilestoneIds] = useState([]);
  const [filterProjectId, setFilterProjectId] = useState(''); // proyecto opcional en modo periodo
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [showExitsDetailModal, setShowExitsDetailModal] = useState(false);
  const [exitsDetailLoading, setExitsDetailLoading] = useState(false);
  const [exitsDetail, setExitsDetail] = useState(null);
  const [exitsDetailProduct, setExitsDetailProduct] = useState(null); // { product_name, warehouse_name } para el título del modal
  const [showComprasDetailModal, setShowComprasDetailModal] = useState(false);
  const [comprasDetailProduct, setComprasDetailProduct] = useState(null); // { product_id, product_name }
  const [showServiciosDetailModal, setShowServiciosDetailModal] = useState(false);
  const [serviciosDetailClassification, setServiciosDetailClassification] = useState(null); // service_name string
  const [showCSDetailModal, setShowCSDetailModal] = useState(false);
  const [csDetailId, setCSDetailId] = useState(null);
  const [csDetailPeriod, setCSDetailPeriod] = useState(null); // month_key para filtrar avances por período
  const [csDetailData, setCSDetailData] = useState(null);
  const [csDetailLoading, setCSDetailLoading] = useState(false);
  const [showProrrateoModal, setShowProrrateoModal] = useState(false);
  const [prorrateoDocRow, setProrrateoDocRow] = useState(null); // fila documento con service_start_date, service_end_date, total_amount, months, label

  useEffect(() => {
    getProjects().then((r) => {
      if (r.success && r.data) setProjects(r.data);
    });
  }, []);

  useEffect(() => {
    if (mode !== 'by_milestones' || !selectedProject) {
      setMilestones([]);
      setSelectedMilestoneIds([]);
      return;
    }
    getMilestones({ project_id: selectedProject }).then((r) => {
      if (r.success && r.data) setMilestones(r.data);
      else setMilestones([]);
    });
    setSelectedMilestoneIds([]);
  }, [mode, selectedProject]);

  const handleConsultar = async (e) => {
    e.preventDefault();
    let dateFrom = '';
    let dateTo = '';
    if (mode === 'by_period') {
      if (!monthFrom || !yearFrom || !monthTo || !yearTo) {
        setToast({ isOpen: true, message: 'Indique mes y año desde y hasta', type: 'error' });
        return;
      }
      const yFrom = parseInt(yearFrom, 10);
      const mFrom = parseInt(monthFrom, 10);
      const yTo = parseInt(yearTo, 10);
      const mTo = parseInt(monthTo, 10);
      if (yFrom > yTo || (yFrom === yTo && mFrom > mTo)) {
        setToast({ isOpen: true, message: 'El mes desde debe ser anterior o igual al mes hasta', type: 'error' });
        return;
      }
      dateFrom = `${yFrom}-${String(mFrom).padStart(2, '0')}-01`;
      // Último día del mes: en JS el mes es 0-indexed, así que mes 2 = marzo, día 0 = último día de febrero
      const lastDay = new Date(yTo, parseInt(mTo, 10), 0);
      dateTo = `${yTo}-${String(mTo).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
    } else {
      if (!selectedProject) {
        setToast({ isOpen: true, message: 'Seleccione un proyecto', type: 'error' });
        return;
      }
      if (selectedMilestoneIds.length === 0) {
        setToast({ isOpen: true, message: 'Seleccione al menos un hito', type: 'error' });
        return;
      }
    }

    setLoading(true);
    try {
      const params = {
        mode,
        project_id: mode === 'by_period' ? (filterProjectId || undefined) : (selectedProject || undefined),
      };
      if (mode === 'by_period') {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      } else {
        params.milestone_ids = selectedMilestoneIds;
      }
      const response = await reportService.getResultsReport(params);
      if (response.success) {
        setReportData(response.data);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar el reporte', type: 'error' });
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al cargar el reporte de resultados',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMilestone = (id) => {
    setSelectedMilestoneIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const parsed = typeof d === 'string' && /^\d{4}-\d{1,2}-\d{1,2}/.test(d.trim()) ? parseLocalDate(d) : new Date(d);
    return parsed && !isNaN(parsed.getTime()) ? parsed.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  };

  const openExitsDetailModal = async (productId, warehouseId, productName, warehouseName) => {
    if (!reportData?.date_from || !reportData?.date_to) return;
    const params = {
      mode,
      project_id: mode === 'by_period' ? (filterProjectId || undefined) : (selectedProject || undefined),
    };
    if (mode === 'by_period') {
      params.date_from = reportData.date_from;
      params.date_to = reportData.date_to;
    } else {
      params.milestone_ids = selectedMilestoneIds;
    }
    if (productId != null && productId !== '') params.product_id = productId;
    if (warehouseId != null && warehouseId !== '') params.warehouse_id = warehouseId;
    setExitsDetailProduct(productName != null && warehouseName != null ? { product_name: productName, warehouse_name: warehouseName } : null);
    setShowExitsDetailModal(true);
    setExitsDetailLoading(true);
    setExitsDetail(null);
    try {
      const response = await reportService.getResultsExitsDetail(params);
      if (response.success && response.data) {
        setExitsDetail(response.data);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar el detalle', type: 'error' });
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al cargar el detalle de salidas',
        type: 'error',
      });
    } finally {
      setExitsDetailLoading(false);
    }
  };

  const closeExitsDetailModal = () => {
    setShowExitsDetailModal(false);
    setExitsDetail(null);
    setExitsDetailProduct(null);
  };

  const openComprasDetailModal = (productId, productName) => {
    setComprasDetailProduct(productId != null && productName != null ? { product_id: productId, product_name: productName } : null);
    setShowComprasDetailModal(true);
  };

  const closeComprasDetailModal = () => {
    setShowComprasDetailModal(false);
    setComprasDetailProduct(null);
  };

  const openServiciosDetailModal = (serviceName) => {
    setServiciosDetailClassification(serviceName != null ? String(serviceName) : null);
    setShowServiciosDetailModal(true);
  };

  const closeServiciosDetailModal = () => {
    setShowServiciosDetailModal(false);
    setServiciosDetailClassification(null);
  };

  const openCSDetailModal = async (sourceId, monthKey) => {
    if (sourceId == null) return;
    setCSDetailId(sourceId);
    setCSDetailPeriod(monthKey != null ? String(monthKey) : null);
    setShowCSDetailModal(true);
    setCSDetailData(null);
    setCSDetailLoading(true);
    try {
      const response = await continuousServiceService.getContinuousService(sourceId);
      if (response.success && response.data) {
        setCSDetailData(response.data);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar el servicio continuo', type: 'error' });
      }
    } catch (err) {
      setToast({ isOpen: true, message: err.response?.data?.message || 'Error al cargar el servicio continuo', type: 'error' });
    } finally {
      setCSDetailLoading(false);
    }
  };

  const closeCSDetailModal = () => {
    setShowCSDetailModal(false);
    setCSDetailId(null);
    setCSDetailPeriod(null);
    setCSDetailData(null);
  };

  const openProrrateoModal = (row) => {
    if (row?.source_type !== 'document' || !row?.service_start_date || !row?.service_end_date) return;
    setProrrateoDocRow(row);
    setShowProrrateoModal(true);
  };

  const closeProrrateoModal = () => {
    setShowProrrateoModal(false);
    setProrrateoDocRow(null);
  };

  // Parsear fecha YYYY-MM-DD como día local (evita desfase de un día por UTC)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr).trim();
    const match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!match) return new Date(dateStr); // fallback
    const [, y, m, d] = match.map(Number);
    return new Date(y, m - 1, d);
  };

  const totalDaysInRange = (dateFrom, dateTo) => {
    const a = parseLocalDate(dateFrom);
    const b = parseLocalDate(dateTo);
    if (!a || !b || isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
    const ms = Math.abs(b - a);
    return Math.floor(ms / (24 * 60 * 60 * 1000)) + 1;
  };

  const overlapDaysInPeriod = (serviceStart, serviceEnd, periodStart, periodEnd) => {
    const rs = parseLocalDate(serviceStart);
    const re = parseLocalDate(serviceEnd);
    const ps = parseLocalDate(periodStart);
    const pe = parseLocalDate(periodEnd);
    if (!rs || !re || !ps || !pe) return 0;
    const start = rs > ps ? rs : ps;
    const end = re < pe ? re : pe;
    if (end < start) return 0;
    return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
  };

  const round4 = (value) => Math.round(Number(value || 0) * 10000) / 10000;

  // Para documentos, recalcular siempre en frontend con la misma fórmula visible,
  // evitando cualquier desfase entre "months" recibido y el cálculo mostrado.
  const getDocumentProratedAmount = (row, block) => {
    if (!row || !block || row.source_type !== 'document' || !row.service_start_date || !row.service_end_date) {
      return row?.months && row.months[block?.month_key] != null ? Number(row.months[block.month_key]) : 0;
    }
    const totalDays = totalDaysInRange(row.service_start_date, row.service_end_date);
    if (totalDays <= 0) return 0;
    const days = overlapDaysInPeriod(row.service_start_date, row.service_end_date, block.date_from, block.date_to);
    if (days <= 0) return 0;
    const totalAmount = Number(row.total_amount ?? 0);
    return round4((totalAmount * days) / totalDays);
  };

  const exportResultsToExcel = async () => {
    if (!reportData) {
      setToast({ isOpen: true, message: 'No hay datos para exportar', type: 'info' });
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Sistema Logistico';
      workbook.created = new Date();

      const fallbackPeriods = Object.keys((reportData?.servicios?.[0]?.months || {})).sort().map((k) => ({ month_key: k }));
      const periodBlocks = byMonth.length > 0 ? byMonth : (byMonthSinAlmacen.length > 0 ? byMonthSinAlmacen : fallbackPeriods);
      const periods = periodBlocks.map((b) => ({
        key: b.month_key,
        label: getPeriodLabel(b),
        date_from: b.date_from,
        date_to: b.date_to,
      }));

      // =======================
      // Hoja 1: Resumen (calco visual)
      // =======================
      const wsSummary = workbook.addWorksheet('Resumen');
      const totalCols = 3 + (periods.length * 2);
      wsSummary.columns = Array.from({ length: totalCols }, (_, i) => ({
        key: `c${i + 1}`,
          width: i < 3 ? [28, 14, 24][i] : 14,
      }));

      const applyThinBorder = (rowNumber) => {
        for (let c = 1; c <= totalCols; c += 1) {
          wsSummary.getCell(rowNumber, c).border = {
            top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          };
        }
      };

      let r = 1;
      wsSummary.mergeCells(r, 1, r, totalCols);
      wsSummary.getCell(r, 1).value = 'REPORTE DE RESULTADOS';
      wsSummary.getCell(r, 1).font = { bold: true, size: 15 };
      r += 1;
      wsSummary.mergeCells(r, 1, r, totalCols);
      wsSummary.getCell(r, 1).value = `Período: ${formatDate(reportData.date_from)} - ${formatDate(reportData.date_to)}`;
      wsSummary.getCell(r, 1).font = { italic: true, color: { argb: 'FF4B5563' } };
      r += 2;

      const writeComparativeSection = (title, tree, includeWarehouse = true) => {
        wsSummary.mergeCells(r, 1, r, totalCols);
        wsSummary.getCell(r, 1).value = title;
        wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF0F766E' }, size: 12 };
        wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6FFFA' } };
        applyThinBorder(r);
        r += 1;

        wsSummary.getCell(r, 1).value = 'Producto';
        wsSummary.getCell(r, 2).value = 'SKU';
        wsSummary.getCell(r, 3).value = includeWarehouse ? 'Almacén' : '';
        wsSummary.getCell(r, 4).value = '';
        periods.forEach((p, idx) => {
          const cStart = 4 + (idx * 2);
          wsSummary.mergeCells(r, cStart, r, cStart + 1);
          wsSummary.getCell(r, cStart).value = p.label;
          wsSummary.getCell(r, cStart).alignment = { horizontal: 'center' };
          wsSummary.getCell(r, cStart).font = { bold: true, color: { argb: 'FFFFFFFF' } };
          wsSummary.getCell(r, cStart).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
          wsSummary.getCell(r + 1, cStart).value = 'Cant.';
          wsSummary.getCell(r + 1, cStart + 1).value = 'Monto';
          wsSummary.getCell(r + 1, cStart).font = { bold: true };
          wsSummary.getCell(r + 1, cStart + 1).font = { bold: true };
        });
        ['A', 'B', 'C'].forEach((col) => {
          wsSummary.getCell(`${col}${r}`).font = { bold: true, color: { argb: 'FFFFFFFF' } };
          wsSummary.getCell(`${col}${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
        });
        applyThinBorder(r);
        applyThinBorder(r + 1);
        r += 2;

        tree.forEach((category) => {
          wsSummary.getCell(r, 1).value = `${category.code || ''} ${category.name || ''}`.trim();
          wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF0F766E' } };
          wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCFBF1' } };
          applyThinBorder(r);
          r += 1;

          category.subcategories.forEach((subcategory) => {
            wsSummary.getCell(r, 1).value = `  ${subcategory.code || ''} ${subcategory.name || 'Sin subcategoría'}`.trim();
            wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF5B21B6' } };
            wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E8FF' } };
            applyThinBorder(r);
            r += 1;

            subcategory.families.forEach((family) => {
              wsSummary.getCell(r, 1).value = `    ${family.code || ''} ${family.name || 'Sin familia'}`.trim();
              wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF374151' } };
              wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
              applyThinBorder(r);
              r += 1;

              family.rows.forEach((row) => {
                wsSummary.getCell(r, 1).value = row.product_name || '-';
                wsSummary.getCell(r, 2).value = row.product_sku || '-';
                wsSummary.getCell(r, 3).value = includeWarehouse ? (row.warehouse_name || '-') : '';
                periods.forEach((p, idx) => {
                  const info = row.months?.[p.key] || { quantity: 0, amount: 0 };
                  const cStart = 4 + (idx * 2);
                  wsSummary.getCell(r, cStart).value = Number(info.quantity || 0);
                  wsSummary.getCell(r, cStart + 1).value = Number(info.amount || 0);
                  wsSummary.getCell(r, cStart).numFmt = '#,##0.000';
                  wsSummary.getCell(r, cStart + 1).numFmt = '"S/ " #,##0.0000';
                  wsSummary.getCell(r, cStart).alignment = { horizontal: 'right' };
                  wsSummary.getCell(r, cStart + 1).alignment = { horizontal: 'right' };
                });
                applyThinBorder(r);
                r += 1;
              });
            });
          });
        });
        r += 1;
      };

      writeComparativeSection('Salida de almacén', hierarchyTree, true);
      writeComparativeSection('Compra directa', hierarchyTreeSinAlmacen, false);

      const writeDepreciacionesSection = (title, tree) => {
        wsSummary.mergeCells(r, 1, r, totalCols);
        wsSummary.getCell(r, 1).value = title;
        wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF0F766E' }, size: 12 };
        wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6FFFA' } };
        applyThinBorder(r);
        r += 1;

        wsSummary.getCell(r, 1).value = 'Código';
        wsSummary.getCell(r, 2).value = 'Activo';
        wsSummary.getCell(r, 3).value = 'Área';
        periods.forEach((p, idx) => {
          wsSummary.getCell(r, 4 + idx).value = `${p.label} (Depreciación)`;
        });
        for (let c = 1; c <= 3 + periods.length; c += 1) {
          wsSummary.getCell(r, c).font = { bold: true, color: { argb: 'FFFFFFFF' } };
          wsSummary.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
        }
        applyThinBorder(r);
        r += 1;

        tree.forEach((category) => {
          wsSummary.getCell(r, 1).value = `${category.code || ''} ${category.name || ''}`.trim();
          wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF0F766E' } };
          wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCFBF1' } };
          applyThinBorder(r);
          r += 1;

          category.subcategories.forEach((subcategory) => {
            wsSummary.getCell(r, 1).value = `  ${subcategory.code || ''} ${subcategory.name || 'Sin subcategoría'}`.trim();
            wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF5B21B6' } };
            wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E8FF' } };
            applyThinBorder(r);
            r += 1;

            subcategory.families.forEach((family) => {
              wsSummary.getCell(r, 1).value = `    ${family.code || ''} ${family.name || 'Sin familia'}`.trim();
              wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF374151' } };
              wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
              applyThinBorder(r);
              r += 1;

              family.rows.forEach((row) => {
                wsSummary.getCell(r, 1).value = row.code || '-';
                wsSummary.getCell(r, 2).value = row.name || '-';
                wsSummary.getCell(r, 3).value = row.area_name || 'Sin área';
                periods.forEach((p, idx) => {
                  const c = 4 + idx;
                  wsSummary.getCell(r, c).value = round4(row.months?.[p.key] || 0);
                  wsSummary.getCell(r, c).numFmt = '"S/ " #,##0.0000';
                  wsSummary.getCell(r, c).alignment = { horizontal: 'right' };
                });
                applyThinBorder(r);
                r += 1;
              });
            });
          });
        });
        r += 1;
      };

      if (activosEnabled && hierarchyTreeDepreciaciones.length > 0) {
        writeDepreciacionesSection('Depreciaciones', hierarchyTreeDepreciaciones);
      }

      // Sección 3: Servicios
      wsSummary.mergeCells(r, 1, r, totalCols);
      wsSummary.getCell(r, 1).value = 'Servicios';
      wsSummary.getCell(r, 1).font = { bold: true, color: { argb: 'FF0F766E' }, size: 12 };
      wsSummary.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6FFFA' } };
      applyThinBorder(r);
      r += 1;
      wsSummary.getCell(r, 1).value = 'Clasificación del servicio';
      periods.forEach((p, idx) => {
        const c = 2 + idx;
        wsSummary.getCell(r, c).value = `${p.label} (Monto sin IGV)`;
      });
      for (let c = 1; c <= (1 + periods.length); c += 1) {
        wsSummary.getCell(r, c).font = { bold: true, color: { argb: 'FFFFFFFF' } };
        wsSummary.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
      }
      r += 1;

      const serviciosRows = reportData.servicios || [];
      const porClasificacion = {};
      serviciosRows.forEach((row) => {
        const clave = row.service_name && String(row.service_name).trim() ? row.service_name : 'Sin clasificación';
        if (!porClasificacion[clave]) porClasificacion[clave] = { service_name: clave, months: {} };
        periods.forEach((p) => {
          const amount = getDocumentProratedAmount(row, {
            month_key: p.key,
            date_from: p.date_from,
            date_to: p.date_to,
          });
          porClasificacion[clave].months[p.key] = (porClasificacion[clave].months[p.key] || 0) + amount;
        });
      });
      Object.values(porClasificacion)
        .sort((a, b) => String(a.service_name || '').localeCompare(String(b.service_name || '')))
        .forEach((row) => {
          wsSummary.getCell(r, 1).value = row.service_name;
          periods.forEach((p, idx) => {
            const c = 2 + idx;
            wsSummary.getCell(r, c).value = round4(row.months[p.key] || 0);
            wsSummary.getCell(r, c).numFmt = '"S/ " #,##0.0000';
            wsSummary.getCell(r, c).alignment = { horizontal: 'right' };
          });
          r += 1;
        });

      // =======================
      // Hoja 2: Detallado por partida
      // =======================
      const detailParams = {
        mode,
        project_id: mode === 'by_period' ? (filterProjectId || undefined) : (selectedProject || undefined),
      };
      if (mode === 'by_period') {
        detailParams.date_from = reportData.date_from;
        detailParams.date_to = reportData.date_to;
      } else {
        detailParams.milestone_ids = selectedMilestoneIds;
      }
      let exitsDetailRowsForExcel = [];
      try {
        const exitsDetailResponse = await reportService.getResultsExitsDetail(detailParams);
        if (exitsDetailResponse?.success && Array.isArray(exitsDetailResponse?.data?.exits)) {
          exitsDetailRowsForExcel = exitsDetailResponse.data.exits;
        }
      } catch (_) {
        // Si falla el detalle de salidas, se continúa exportando con el resto de secciones.
      }

      const splitGuideCode = (code) => {
        if (!code) return { serie: '', numero: '' };
        const value = String(code).trim();
        if (!value) return { serie: '', numero: '' };
        const idx = value.indexOf('-');
        if (idx <= 0 || idx >= value.length - 1) return { serie: value, numero: '' };
        return {
          serie: value.substring(0, idx).trim(),
          numero: value.substring(idx + 1).trim(),
        };
      };
      const getServiceFamilyForExcel = (row) => {
        if (!row) return '';
        const fromServiceName = String(row.service_name || '').trim();
        if (fromServiceName) {
          return fromServiceName;
        }
        const fromLabel = String(row.label || '').trim();
        if (fromLabel) {
          // Evitar arrastrar sufijos del documento o rango cuando no hay service_name
          const withoutDocSuffix = fromLabel.replace(/\s+[–-]\s+DOC.*$/i, '').trim();
          const withoutRangeSuffix = withoutDocSuffix.replace(/\s+\(.*\)\s*$/, '').trim();
          return withoutRangeSuffix || fromLabel;
        }
        return '';
      };

      const milestonePeriods = reportData?.milestone_periods || [];
      const periodByKey = {};
      periods.forEach((p) => {
        periodByKey[p.key] = p;
      });
      const resolvePeriodKeyByDate = (dateStr) => {
        if (!dateStr) return null;
        const d = String(dateStr).substring(0, 10);
        if (mode === 'by_milestones' && milestonePeriods.length > 0) {
          const found = milestonePeriods.find((p) => d >= p.date_from && d <= p.date_to);
          return found ? found.key : null;
        }
        return d.length >= 7 ? d.substring(0, 7) : null;
      };

      const classByProductWarehouse = {};
      comparativeRows.forEach((row) => {
        classByProductWarehouse[`${row.product_id}_${row.warehouse_id}`] = row;
      });

      const wsDetail = workbook.addWorksheet('Detallado por partida');
      // Sin `header` en columns: si no, ExcelJS vuelca una fila de cabecera automática y queda duplicada con addRow de abajo.
      wsDetail.columns = [
        { key: 'seccion', width: 24 },
        { key: 'categoria', width: 20 },
        { key: 'subcategoria', width: 20 },
        { key: 'familia', width: 20 },
        { key: 'partida', width: 38 },
        { key: 'almacen', width: 26 },
        { key: 'serie_guia', width: 16 },
        { key: 'numero_guia', width: 18 },
        { key: 'periodo', width: 28 },
        { key: 'cantidad', width: 14 },
        { key: 'monto', width: 18 },
        { key: 'maquinaria', width: 46 },
        { key: 'origen', width: 18 },
        { key: 'estado', width: 14 },
      ];

      const dt = wsDetail.addRow(['DETALLADO POR PARTIDA']);
      wsDetail.mergeCells(`A${dt.number}:N${dt.number}`);
      dt.getCell(1).font = { bold: true, size: 13 };
      wsDetail.addRow([]);
      const dh = wsDetail.addRow([
        'Seccion', 'Categoria', 'Subcategoria', 'Familia', 'Partida', 'Almacen', 'Serie guia', 'Numero guia', 'Periodo', 'Cantidad', 'Monto sin IGV', 'Vehiculo / Maquinaria', 'Origen', 'Estado',
      ]);
      dh.eachCell((c) => {
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
      });

      exitsDetailRowsForExcel.forEach((row) => {
        const periodKey = resolvePeriodKeyByDate(row.movement_date);
        if (!periodKey || !periodByKey[periodKey]) return;
        const periodInfo = periodByKey[periodKey];
        const classRow = classByProductWarehouse[`${row.product_id}_${row.warehouse_id}`];
        const { serie, numero } = splitGuideCode(row.entry_guide_code);
        wsDetail.addRow([
          'Salida de almacén',
          row.category_name || classRow?.category_name || '',
          row.subcategory_name || classRow?.subcategory_name || '',
          row.family_name || classRow?.family_name || '',
          `${row.product_name || '-'} (${row.product_sku || '-'})`,
          row.warehouse_name || '',
          serie,
          numero,
          periodInfo.label,
          round4(row.quantity),
          round4(row.total),
          row.vehicle_label || row.machinery_label || '',
          'Kardex',
          row.tipo_label || '',
        ]);
      });

      const comprasSinAlmacenItems = reportData.compras_sin_almacen || [];
      const classByProductSinAlmacen = {};
      comparativeRowsSinAlmacen.forEach((row) => {
        classByProductSinAlmacen[String(row.product_id)] = row;
      });
      comprasSinAlmacenItems.forEach((item) => {
        const periodKey = resolvePeriodKeyByDate(item.issue_date);
        if (!periodKey || !periodByKey[periodKey]) return;
        const periodInfo = periodByKey[periodKey];
        const classRow = classByProductSinAlmacen[String(item.product_id)] || {};
        wsDetail.addRow([
          'Compra directa',
          item.category_name || classRow.category_name || '',
          item.subcategory_name || classRow.subcategory_name || '',
          item.family_name || classRow.family_name || '',
          `${item.product_name || '-'} (${item.product_sku || '-'})`,
          '',
          item.document_series || '',
          item.document_number || '',
          periodInfo.label,
          round4(item.quantity),
          round4(item.subtotal ?? item.total ?? 0),
          item.vehicle_label || item.machinery_label || '',
          item.document_type === 'suministro_continuo' ? 'Suministro continuo' : 'Documento',
          '',
        ]);
      });

      (reportData.servicios || []).forEach((row) => {
        periods.forEach((p) => {
          const amount = getDocumentProratedAmount(row, { month_key: p.key, date_from: p.date_from, date_to: p.date_to });
          if (!Number(amount)) return;
          const isContinuous = row.source_type === 'continuous_service';
          const continuousCode = isContinuous && row.label
            ? String(row.label).split(' ')[0]
            : '';
          wsDetail.addRow([
            'Servicios',
            '',
            '',
            getServiceFamilyForExcel(row),
            row.label || row.service_name || '',
            '',
            isContinuous ? 'SC' : (row.document_series || ''),
            isContinuous ? continuousCode : (row.document_number || ''),
            p.label,
            '',
            round4(amount),
            row.vehicle_label || row.machinery_label || '',
            isContinuous ? 'Servicio continuo' : 'Documento',
            isContinuous ? (row.status === 'open' ? 'Abierto' : 'Cerrado') : '',
          ]);
        });
      });

      wsDetail.eachRow((row, idx) => {
        if (idx >= 4) {
          row.getCell(10).numFmt = '#,##0.000';
          row.getCell(11).numFmt = '"S/ " #,##0.0000';
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Reporte_Resultados_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al exportar a Excel', type: 'error' });
    }
  };

  const getMonthLabel = (monthKey) => {
    if (!monthKey) return '';
    const [y, m] = monthKey.split('-').map(Number);
    return `${monthNames[m - 1]} ${y}`;
  };

  const getPeriodLabel = (blockOrKey) => {
    const key = typeof blockOrKey === 'object' && blockOrKey != null ? blockOrKey.month_key : blockOrKey;
    if (!key) return '';
    const milestonePeriods = reportData?.milestone_periods || [];
    const milestone = milestonePeriods.find((p) => p.key === key);
    if (milestone) {
      return `${milestone.name} (${milestone.date_from} – ${milestone.date_to})`;
    }
    return getMonthLabel(key);
  };

  const byMonth = reportData?.calcula_productos_by_month || [];
  const byMonthSinAlmacen = reportData?.compras_sin_almacen_by_month || [];

  const depreciationPeriods = React.useMemo(() => {
    if (byMonth.length > 0) return byMonth;
    const milestonePeriods = reportData?.milestone_periods || [];
    if (milestonePeriods.length > 0) {
      return milestonePeriods.map((p) => ({
        month_key: p.key,
        date_from: p.date_from,
        date_to: p.date_to,
      }));
    }
    const monthKeys = new Set();
    (reportData?.depreciaciones || []).forEach((row) => {
      Object.keys(row.months || {}).forEach((k) => monthKeys.add(k));
    });
    return [...monthKeys].sort().map((key) => ({ month_key: key }));
  }, [byMonth, reportData?.milestone_periods, reportData?.depreciaciones]);

  const depreciacionesRows = React.useMemo(
    () => reportData?.depreciaciones || [],
    [reportData?.depreciaciones]
  );

  const hierarchyTreeDepreciaciones = React.useMemo(() => {
    const catMap = {};
    depreciacionesRows.forEach((row) => {
      const cid = row.category_id ?? 0;
      const cname = row.category_name || 'Sin clasificación';
      const ccode = row.category_code || (cid === 0 ? 'SIN-CAT' : '');
      if (!catMap[cid]) {
        catMap[cid] = { id: cid, code: ccode, name: cname, subcategories: {} };
      }
      const subid = row.subcategory_id ?? 0;
      const subname = row.subcategory_name || '';
      const subcode = row.subcategory_code || '';
      if (!catMap[cid].subcategories[subid]) {
        catMap[cid].subcategories[subid] = { id: subid, code: subcode, name: subname, families: {} };
      }
      const fid = row.family_id ?? 0;
      const fname = row.family_name || '';
      const fcode = row.family_code || '';
      if (!catMap[cid].subcategories[subid].families[fid]) {
        catMap[cid].subcategories[subid].families[fid] = { id: fid, code: fcode, name: fname, rows: [] };
      }
      catMap[cid].subcategories[subid].families[fid].rows.push(row);
    });
    const categories = Object.values(catMap).map((c) => ({
      ...c,
      subcategories: Object.values(c.subcategories).map((s) => ({
        ...s,
        families: Object.values(s.families).filter((f) => f.rows.length > 0),
      })),
    }));
    const sortName = (a, b) => (a.name || '').localeCompare(b.name || '', 'es');
    categories.forEach((c) => {
      c.subcategories = c.subcategories.filter((s) => s.families.length > 0);
      c.subcategories.sort(sortName);
      c.subcategories.forEach((s) => s.families.sort(sortName));
    });
    const filtered = categories.filter((c) => c.subcategories.length > 0);
    filtered.sort((a, b) => {
      if (a.name === 'Sin clasificación') return 1;
      if (b.name === 'Sin clasificación') return -1;
      return sortName(a, b);
    });
    return filtered;
  }, [depreciacionesRows]);

  // Construir filas comparativas con clasificación (una fila por producto/almacén, columnas por mes)
  const comparativeRows = React.useMemo(() => {
    const rowMap = {};
    byMonth.forEach((block) => {
      block.data.forEach((row) => {
        const key = `${row.product_id}_${row.warehouse_id}`;
        if (!rowMap[key]) {
          rowMap[key] = {
            product_id: row.product_id,
            product_name: row.product_name || '-',
            product_sku: row.product_sku || '-',
            warehouse_id: row.warehouse_id,
            warehouse_name: row.warehouse_name || '-',
            months: {},
            machinery_label: row.machinery_label || '',
            // Clasificación (para agrupar por jerarquía)
            category_id: row.category_id,
            category_code: row.category_code || '',
            category_name: row.category_name || 'Sin clasificación',
            subcategory_id: row.subcategory_id,
            subcategory_code: row.subcategory_code || '',
            subcategory_name: row.subcategory_name || '',
            family_id: row.family_id,
            family_code: row.family_code || '',
            family_name: row.family_name || '',
          };
        }
        rowMap[key].months[block.month_key] = {
          quantity: row.exits_quantity ?? 0,
          amount: row.exits_amount ?? 0,
        };
        if (row.machinery_label && String(row.machinery_label).trim() !== '') {
          const current = String(rowMap[key].machinery_label || '').trim();
          const incoming = String(row.machinery_label).trim();
          if (!current) rowMap[key].machinery_label = incoming;
          else if (!current.split(' ; ').includes(incoming)) rowMap[key].machinery_label = `${current} ; ${incoming}`;
        }
      });
    });
    // Ocultar filas donde en todos los meses cantidad y monto son cero
    return Object.values(rowMap).filter((row) => {
      const vals = Object.values(row.months);
      if (vals.length === 0) return false;
      return vals.some((m) => m.quantity !== 0 || m.amount !== 0);
    });
  }, [byMonth]);

  // Agrupar filas por jerarquía: categoría → subcategoría → familia → filas
  const hierarchyTree = React.useMemo(() => {
    const catMap = {};
    comparativeRows.forEach((row) => {
      const cid = row.category_id ?? 0;
      const cname = row.category_name || 'Sin clasificación';
      const ccode = row.category_code || (cid === 0 ? 'SIN-CAT' : '');
      if (!catMap[cid]) {
        catMap[cid] = { id: cid, code: ccode, name: cname, subcategories: {} };
      }
      const subid = row.subcategory_id ?? 0;
      const subname = row.subcategory_name || '';
      const subcode = row.subcategory_code || '';
      if (!catMap[cid].subcategories[subid]) {
        catMap[cid].subcategories[subid] = { id: subid, code: subcode, name: subname, families: {} };
      }
      const fid = row.family_id ?? 0;
      const fname = row.family_name || '';
      const fcode = row.family_code || '';
      if (!catMap[cid].subcategories[subid].families[fid]) {
        catMap[cid].subcategories[subid].families[fid] = { id: fid, code: fcode, name: fname, rows: [] };
      }
      catMap[cid].subcategories[subid].families[fid].rows.push(row);
    });
    const categories = Object.values(catMap).map((c) => ({
      ...c,
      subcategories: Object.values(c.subcategories).map((s) => ({
        ...s,
        families: Object.values(s.families).filter((f) => f.rows.length > 0),
      })),
    }));
    const sortName = (a, b) => (a.name || '').localeCompare(b.name || '');
    categories.forEach((c) => {
      c.subcategories = c.subcategories.filter((s) => s.families.length > 0);
      c.subcategories.sort(sortName);
      c.subcategories.forEach((s) => s.families.sort(sortName));
    });
    const filtered = categories.filter((c) => c.subcategories.length > 0);
    filtered.sort((a, b) => {
      if (a.name === 'Sin clasificación') return 1;
      if (b.name === 'Sin clasificación') return -1;
      return sortName(a, b);
    });
    return filtered;
  }, [comparativeRows]);

  // Segunda sección: filas comparativas por producto (sin almacén), cantidad y monto sin IGV por mes
  const comparativeRowsSinAlmacen = React.useMemo(() => {
    const rowMap = {};
    byMonthSinAlmacen.forEach((block) => {
      block.data.forEach((row) => {
        const key = String(row.product_id);
        if (!rowMap[key]) {
          rowMap[key] = {
            product_id: row.product_id,
            product_name: row.product_name || '-',
            product_sku: row.product_sku || '-',
            months: {},
            machinery_label: row.machinery_label || '',
            category_id: row.category_id,
            category_code: row.category_code || '',
            category_name: row.category_name || 'Sin clasificación',
            subcategory_id: row.subcategory_id,
            subcategory_code: row.subcategory_code || '',
            subcategory_name: row.subcategory_name || '',
            family_id: row.family_id,
            family_code: row.family_code || '',
            family_name: row.family_name || '',
          };
        }
        rowMap[key].months[block.month_key] = {
          quantity: row.quantity ?? 0,
          amount: row.amount_sin_igv ?? 0,
        };
        if (row.machinery_label && String(row.machinery_label).trim() !== '') {
          const current = String(rowMap[key].machinery_label || '').trim();
          const incoming = String(row.machinery_label).trim();
          if (!current) rowMap[key].machinery_label = incoming;
          else if (!current.split(' ; ').includes(incoming)) rowMap[key].machinery_label = `${current} ; ${incoming}`;
        }
      });
    });
    return Object.values(rowMap).filter((row) => {
      const vals = Object.values(row.months);
      if (vals.length === 0) return false;
      return vals.some((m) => m.quantity !== 0 || m.amount !== 0);
    });
  }, [byMonthSinAlmacen]);

  // Jerarquía para segunda sección (compra directa)
  const hierarchyTreeSinAlmacen = React.useMemo(() => {
    const catMap = {};
    comparativeRowsSinAlmacen.forEach((row) => {
      const cid = row.category_id ?? 0;
      const cname = row.category_name || 'Sin clasificación';
      const ccode = row.category_code || (cid === 0 ? 'SIN-CAT' : '');
      if (!catMap[cid]) {
        catMap[cid] = { id: cid, code: ccode, name: cname, subcategories: {} };
      }
      const subid = row.subcategory_id ?? 0;
      const subname = row.subcategory_name || '';
      const subcode = row.subcategory_code || '';
      if (!catMap[cid].subcategories[subid]) {
        catMap[cid].subcategories[subid] = { id: subid, code: subcode, name: subname, families: {} };
      }
      const fid = row.family_id ?? 0;
      const fname = row.family_name || '';
      const fcode = row.family_code || '';
      if (!catMap[cid].subcategories[subid].families[fid]) {
        catMap[cid].subcategories[subid].families[fid] = { id: fid, code: fcode, name: fname, rows: [] };
      }
      catMap[cid].subcategories[subid].families[fid].rows.push(row);
    });
    const categories = Object.values(catMap).map((c) => ({
      ...c,
      subcategories: Object.values(c.subcategories).map((s) => ({
        ...s,
        families: Object.values(s.families).filter((f) => f.rows.length > 0),
      })),
    }));
    const sortName = (a, b) => (a.name || '').localeCompare(b.name || '');
    categories.forEach((c) => {
      c.subcategories = c.subcategories.filter((s) => s.families.length > 0);
      c.subcategories.sort(sortName);
      c.subcategories.forEach((s) => s.families.sort(sortName));
    });
    const filtered = categories.filter((c) => c.subcategories.length > 0);
    filtered.sort((a, b) => {
      if (a.name === 'Sin clasificación') return 1;
      if (b.name === 'Sin clasificación') return -1;
      return sortName(a, b);
    });
    return filtered;
  }, [comparativeRowsSinAlmacen]);

  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());
  const [expandedCategories2, setExpandedCategories2] = useState(new Set());
  const [expandedSubcategories2, setExpandedSubcategories2] = useState(new Set());
  const [expandedDepCategories, setExpandedDepCategories] = useState(new Set());
  const [expandedDepSubcategories, setExpandedDepSubcategories] = useState(new Set());

  const toggleCategory = (id) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSubcategory = (catId, subId) => {
    const key = `${catId}_${subId}`;
    setExpandedSubcategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleCategory2 = (id) => {
    setExpandedCategories2((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSubcategory2 = (catId, subId) => {
    const key = `${catId}_${subId}`;
    setExpandedSubcategories2((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleDepCategory = (id) => {
    setExpandedDepCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDepSubcategory = (catId, subId) => {
    const key = `${catId}_${subId}`;
    setExpandedDepSubcategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Expandir todo por defecto cuando hay datos
  React.useEffect(() => {
    if (hierarchyTree.length === 0) return;
    const catIds = new Set(hierarchyTree.map((c) => c.id));
    setExpandedCategories(catIds);
    const subKeys = new Set();
    hierarchyTree.forEach((c) =>
      c.subcategories.forEach((s) => subKeys.add(`${c.id}_${s.id}`))
    );
    setExpandedSubcategories(subKeys);
  }, [reportData?.date_from, reportData?.date_to]);

  React.useEffect(() => {
    if (hierarchyTreeSinAlmacen.length === 0) return;
    const catIds = new Set(hierarchyTreeSinAlmacen.map((c) => c.id));
    setExpandedCategories2(catIds);
    const subKeys = new Set();
    hierarchyTreeSinAlmacen.forEach((c) =>
      c.subcategories.forEach((s) => subKeys.add(`${c.id}_${s.id}`))
    );
    setExpandedSubcategories2(subKeys);
  }, [reportData?.date_from, reportData?.date_to]);

  React.useEffect(() => {
    if (hierarchyTreeDepreciaciones.length === 0) return;
    const catIds = new Set(hierarchyTreeDepreciaciones.map((c) => c.id));
    setExpandedDepCategories(catIds);
    const subKeys = new Set();
    hierarchyTreeDepreciaciones.forEach((c) =>
      c.subcategories.forEach((s) => subKeys.add(`${c.id}_${s.id}`))
    );
    setExpandedDepSubcategories(subKeys);
  }, [reportData?.date_from, reportData?.date_to, hierarchyTreeDepreciaciones.length]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <nav className="text-sm text-gray-600 mb-2">
          <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
          <span className="mx-2">/</span>
          <Link to="/administration" className="hover:text-teal-600">Administración</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Reportes</span>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Resultados</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-800">Resultados</h1>
      </div>

      {/* Filtros */}
      <form onSubmit={handleConsultar} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end mb-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={mode === 'by_period'}
                onChange={() => setMode('by_period')}
                className="text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm font-medium text-gray-700">Por periodo</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={mode === 'by_milestones'}
                onChange={() => setMode('by_milestones')}
                className="text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm font-medium text-gray-700">Por hitos</span>
            </label>
          </div>
        </div>

        {mode === 'by_period' && (
          <div className="flex flex-wrap gap-4 items-end mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde (mes / año)</label>
              <div className="flex gap-2">
                <select
                  value={monthFrom}
                  onChange={(e) => setMonthFrom(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[120px]"
                >
                  <option value="">Mes</option>
                  {monthNames.map((name, i) => (
                    <option key={i + 1} value={i + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={yearFrom}
                  onChange={(e) => setYearFrom(Number(e.target.value))}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[90px]"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta (mes / año)</label>
              <div className="flex gap-2">
                <select
                  value={monthTo}
                  onChange={(e) => setMonthTo(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[120px]"
                >
                  <option value="">Mes</option>
                  {monthNames.map((name, i) => (
                    <option key={i + 1} value={i + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={yearTo}
                  onChange={(e) => setYearTo(Number(e.target.value))}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[90px]"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proyecto</label>
              <select
                value={filterProjectId}
                onChange={(e) => setFilterProjectId(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[220px]"
              >
                <option value="">Todos los proyectos (todos los almacenes)</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} – {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {mode === 'by_milestones' && (
          <div className="flex flex-wrap gap-4 items-end mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proyecto</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[220px]"
              >
                <option value="">Seleccione un proyecto</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} – {p.name}
                  </option>
                ))}
              </select>
            </div>
            {milestones.length > 0 && (
              <div className="flex-1 min-w-[280px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Hitos (seleccione uno o más)</label>
                <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-2 bg-gray-50 max-h-32 overflow-y-auto">
                  {milestones.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedMilestoneIds.includes(m.id)}
                        onChange={() => toggleMilestone(m.id)}
                        className="rounded text-teal-600 focus:ring-teal-500"
                      />
                      <span>
                        {m.name} ({formatDate(m.due_date)}
                        {m.end_date ? ` – ${formatDate(m.end_date)}` : ''})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-4 items-end mb-4">
          {mode === 'by_milestones' && (
            <div className="text-sm text-gray-600">
              Se calculan todos los almacenes para el proyecto e hitos seleccionados.
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
          >
            {loading ? 'Consultando...' : 'Consultar'}
          </button>
        </div>
      </form>

      {reportData && (
        <>
          {reportData.date_from && reportData.date_to && (
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                Período: {formatDate(reportData.date_from)} – {formatDate(reportData.date_to)}
              </p>
              <button
                type="button"
                onClick={exportResultsToExcel}
                className="px-3 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700"
              >
                Descargar Excel
              </button>
            </div>
          )}

          {/* Sección 1: Salida de almacén — comparativo por meses (Enero abajo cant/monto, Febrero abajo cant/monto, etc.) */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Salida de almacén
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Salidas del kardex comparativo por mes (las devoluciones en negativo). Haga clic en <strong>Ver detalle</strong> en una fila para ver todas las salidas de ese producto en ese almacén.
            </p>

            {byMonth.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                No hay salidas en el período seleccionado.
              </div>
            ) : (
              <div className="space-y-4">
                {hierarchyTree.map((category) => (
                  <div key={`cat-${category.id}`} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                    {/* Nivel categoría */}
                    <div
                      className="bg-teal-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors"
                      onClick={() => toggleCategory(category.id)}
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          className={`w-5 h-5 text-teal-600 transition-transform ${expandedCategories.has(category.id) ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="font-semibold text-teal-800">{category.code || category.name}</span>
                        {category.code && <span className="text-gray-800 font-medium">{category.name}</span>}
                        <span className="text-xs text-gray-500">(Categoría)</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {category.subcategories.length} subcategoría(s)
                      </span>
                    </div>

                    {expandedCategories.has(category.id) && category.subcategories.length > 0 && (
                      <div className="border-t border-gray-200">
                        {category.subcategories.map((subcategory) => {
                          const subKey = `${category.id}_${subcategory.id}`;
                          const isSubExpanded = expandedSubcategories.has(subKey);
                          return (
                            <div key={`sub-${subcategory.id}`} className="border-t border-gray-200 first:border-t-0">
                              {/* Nivel subcategoría */}
                              <div
                                className="bg-purple-50 px-6 py-2.5 flex items-center justify-between cursor-pointer hover:bg-purple-100 transition-colors"
                                onClick={() => toggleSubcategory(category.id, subcategory.id)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-4 h-4 border-l-2 border-t-2 border-purple-300" />
                                  <span className="font-semibold text-purple-800">{subcategory.code || subcategory.name || '—'}</span>
                                  {subcategory.code && subcategory.name && <span className="text-gray-800">{subcategory.name}</span>}
                                  {!subcategory.code && !subcategory.name && <span className="text-gray-500">Sin subcategoría</span>}
                                  <span className="text-xs text-gray-500">(Subcategoría)</span>
                                </div>
                                <span className="text-sm text-gray-600">
                                  {subcategory.families.length} familia(s)
                                </span>
                              </div>

                              {isSubExpanded && subcategory.families.length > 0 && (
                                <div className="bg-gray-50/50">
                                  {subcategory.families.map((family) => (
                                    <div key={`fam-${family.id}`} className="border-t border-gray-200 first:border-t-0">
                                      {/* Nivel familia + tabla de productos */}
                                      <div className="px-8 py-2 flex items-center bg-gray-100/80 border-b border-gray-200">
                                        <div className="w-4 h-4 border-l-2 border-t-2 border-gray-400" />
                                        <span className="font-semibold text-gray-800 ml-2">{family.code || family.name || '—'}</span>
                                        {family.code && family.name && <span className="text-gray-700 ml-1">{family.name}</span>}
                                        {!family.code && !family.name && <span className="text-gray-500 ml-1">Sin familia</span>}
                                        <span className="text-xs text-gray-500 ml-2">(Familia) — {family.rows.length} producto(s)</span>
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                          <thead className="bg-gray-100">
                                            <tr>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Almacén</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Detalle</th>
                                              {byMonth.map((block) => (
                                                <React.Fragment key={block.month_key}>
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{getPeriodLabel(block)} (Cant.)</th>
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{getPeriodLabel(block.month_key)} (Monto)</th>
                                                </React.Fragment>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody className="bg-white divide-y divide-gray-200">
                                            {family.rows.map((row, idx) => (
                                              <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.product_name}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">{row.product_sku}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">{row.warehouse_name}</td>
                                                <td className="px-3 py-2">
                                                  <button
                                                    type="button"
                                                    onClick={() => openExitsDetailModal(row.product_id, row.warehouse_id, row.product_name, row.warehouse_name)}
                                                    className="text-sm font-medium text-teal-600 hover:text-teal-800 underline"
                                                  >
                                                    Ver detalle
                                                  </button>
                                                </td>
                                                {byMonth.map((block) => {
                                                  const val = row.months[block.month_key];
                                                  const qty = val ? val.quantity : 0;
                                                  const amt = val ? val.amount : 0;
                                                  return (
                                                    <React.Fragment key={block.month_key}>
                                                      <td className="px-3 py-2 text-sm text-right">
                                                        <span className={qty < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                                          {Number(qty).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2 text-sm text-right">
                                                        <span className={amt < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                                          S/ {Number(amt).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                                        </span>
                                                      </td>
                                                    </React.Fragment>
                                                  );
                                                })}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sección 2: Compra directa — por meses, monto sin IGV, misma lógica que sección 1 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Compra directa
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Ítems de consumo directo (compras o suministros continuos) desglosados por mes. Cantidad y monto sin IGV. Mismo período y filtro de proyecto. Haga clic en <strong>Ver detalle</strong> en una fila para ver los documentos/ítems de ese producto.
            </p>

            {byMonthSinAlmacen.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                No hay ítems de consumo directo en el período seleccionado.
              </div>
            ) : (
              <div className="space-y-4">
                {hierarchyTreeSinAlmacen.map((category) => (
                  <div key={`s2-cat-${category.id}`} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                    <div
                      className="bg-teal-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors"
                      onClick={() => toggleCategory2(category.id)}
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          className={`w-5 h-5 text-teal-600 transition-transform ${expandedCategories2.has(category.id) ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="font-semibold text-teal-800">{category.code || category.name}</span>
                        {category.code && <span className="text-gray-800 font-medium">{category.name}</span>}
                        <span className="text-xs text-gray-500">(Categoría)</span>
                      </div>
                      <span className="text-sm text-gray-600">{category.subcategories.length} subcategoría(s)</span>
                    </div>

                    {expandedCategories2.has(category.id) && category.subcategories.length > 0 && (
                      <div className="border-t border-gray-200">
                        {category.subcategories.map((subcategory) => {
                          const subKey = `${category.id}_${subcategory.id}`;
                          const isSubExpanded = expandedSubcategories2.has(subKey);
                          return (
                            <div key={`s2-sub-${subcategory.id}`} className="border-t border-gray-200 first:border-t-0">
                              <div
                                className="bg-purple-50 px-6 py-2.5 flex items-center justify-between cursor-pointer hover:bg-purple-100 transition-colors"
                                onClick={() => toggleSubcategory2(category.id, subcategory.id)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-4 h-4 border-l-2 border-t-2 border-purple-300" />
                                  <span className="font-semibold text-purple-800">{subcategory.code || subcategory.name || '—'}</span>
                                  {subcategory.code && subcategory.name && <span className="text-gray-800">{subcategory.name}</span>}
                                  {!subcategory.code && !subcategory.name && <span className="text-gray-500">Sin subcategoría</span>}
                                  <span className="text-xs text-gray-500">(Subcategoría)</span>
                                </div>
                                <span className="text-sm text-gray-600">{subcategory.families.length} familia(s)</span>
                              </div>

                              {isSubExpanded && subcategory.families.length > 0 && (
                                <div className="bg-gray-50/50">
                                  {subcategory.families.map((family) => (
                                    <div key={`s2-fam-${family.id}`} className="border-t border-gray-200 first:border-t-0">
                                      <div className="px-8 py-2 flex items-center bg-gray-100/80 border-b border-gray-200">
                                        <div className="w-4 h-4 border-l-2 border-t-2 border-gray-400" />
                                        <span className="font-semibold text-gray-800 ml-2">{family.code || family.name || '—'}</span>
                                        {family.code && family.name && <span className="text-gray-700 ml-1">{family.name}</span>}
                                        {!family.code && !family.name && <span className="text-gray-500 ml-1">Sin familia</span>}
                                        <span className="text-xs text-gray-500 ml-2">(Familia) — {family.rows.length} producto(s)</span>
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                          <thead className="bg-gray-100">
                                            <tr>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Detalle</th>
                                              {byMonthSinAlmacen.map((block) => (
                                                <React.Fragment key={block.month_key}>
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{getPeriodLabel(block)} (Cant.)</th>
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{getPeriodLabel(block)} (Monto sin IGV)</th>
                                                </React.Fragment>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody className="bg-white divide-y divide-gray-200">
                                            {family.rows.map((row, idx) => (
                                              <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.product_name}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">{row.product_sku}</td>
                                                <td className="px-3 py-2">
                                                  <button
                                                    type="button"
                                                    onClick={() => openComprasDetailModal(row.product_id, row.product_name)}
                                                    className="text-sm font-medium text-teal-600 hover:text-teal-800 underline"
                                                  >
                                                    Ver detalle
                                                  </button>
                                                </td>
                                                {byMonthSinAlmacen.map((block) => {
                                                  const val = row.months[block.month_key];
                                                  const qty = val ? val.quantity : 0;
                                                  const amt = val ? val.amount : 0;
                                                  return (
                                                    <React.Fragment key={block.month_key}>
                                                      <td className="px-3 py-2 text-sm text-right">
                                                        <span className={qty < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                                          {Number(qty).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2 text-sm text-right">
                                                        <span className={amt < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                                          S/ {Number(amt).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                                        </span>
                                                      </td>
                                                    </React.Fragment>
                                                  );
                                                })}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {activosEnabled && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Depreciaciones
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Depreciación de activos fijos en el período seleccionado (montos sin IGV), agrupada por clasificación de activos.
              </p>

              {hierarchyTreeDepreciaciones.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                  No hay depreciaciones en el período seleccionado.
                </div>
              ) : (
                <div className="space-y-4">
                  {hierarchyTreeDepreciaciones.map((category) => (
                    <div key={`dep-cat-${category.id}`} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                      <div
                        className="bg-teal-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors"
                        onClick={() => toggleDepCategory(category.id)}
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className={`w-5 h-5 text-teal-600 transition-transform ${expandedDepCategories.has(category.id) ? 'rotate-90' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="font-semibold text-teal-800">{category.code || category.name}</span>
                          {category.code && <span className="text-gray-800 font-medium">{category.name}</span>}
                          <span className="text-xs text-gray-500">(Categoría)</span>
                        </div>
                        <span className="text-sm text-gray-600">
                          {category.subcategories.length} subcategoría(s)
                        </span>
                      </div>

                      {expandedDepCategories.has(category.id) && category.subcategories.length > 0 && (
                        <div className="border-t border-gray-200">
                          {category.subcategories.map((subcategory) => {
                            const subKey = `${category.id}_${subcategory.id}`;
                            const isSubExpanded = expandedDepSubcategories.has(subKey);
                            return (
                              <div key={`dep-sub-${subcategory.id}`} className="border-t border-gray-200 first:border-t-0">
                                <div
                                  className="bg-purple-50 px-6 py-2.5 flex items-center justify-between cursor-pointer hover:bg-purple-100 transition-colors"
                                  onClick={() => toggleDepSubcategory(category.id, subcategory.id)}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 border-l-2 border-t-2 border-purple-300" />
                                    <span className="font-semibold text-purple-800">{subcategory.code || subcategory.name || '—'}</span>
                                    {subcategory.code && subcategory.name && <span className="text-gray-800">{subcategory.name}</span>}
                                    {!subcategory.code && !subcategory.name && <span className="text-gray-500">Sin subcategoría</span>}
                                    <span className="text-xs text-gray-500">(Subcategoría)</span>
                                  </div>
                                  <span className="text-sm text-gray-600">
                                    {subcategory.families.length} familia(s)
                                  </span>
                                </div>

                                {isSubExpanded && subcategory.families.length > 0 && (
                                  <div className="bg-gray-50/50">
                                    {subcategory.families.map((family) => (
                                      <div key={`dep-fam-${family.id}`} className="border-t border-gray-200 first:border-t-0">
                                        <div className="px-8 py-2 flex items-center bg-gray-100/80 border-b border-gray-200">
                                          <div className="w-4 h-4 border-l-2 border-t-2 border-gray-400" />
                                          <span className="font-semibold text-gray-800 ml-2">{family.code || family.name || '—'}</span>
                                          {family.code && family.name && <span className="text-gray-700 ml-1">{family.name}</span>}
                                          {!family.code && !family.name && <span className="text-gray-500 ml-1">Sin familia</span>}
                                          <span className="text-xs text-gray-500 ml-2">(Familia) — {family.rows.length} activo(s)</span>
                                        </div>
                                        <div className="overflow-x-auto">
                                          <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                              <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Activo</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Área</th>
                                                {depreciationPeriods.map((block) => (
                                                  <th key={block.month_key} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                                    {getPeriodLabel(block)} (Depreciación)
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                              {family.rows.map((row) => (
                                                <tr key={row.activo_id} className="hover:bg-gray-50">
                                                  <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.code}</td>
                                                  <td className="px-3 py-2 text-sm text-gray-900">{row.name}</td>
                                                  <td className="px-3 py-2 text-sm text-gray-600">{row.area_name || 'Sin área'}</td>
                                                  {depreciationPeriods.map((block) => {
                                                    const amt = row.months?.[block.month_key] != null ? Number(row.months[block.month_key]) : 0;
                                                    return (
                                                      <td key={block.month_key} className="px-3 py-2 text-sm text-right font-medium">
                                                        S/ {amt.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                                      </td>
                                                    );
                                                  })}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sección 3: Servicios — totales por clasificación (solo servicios continuos, sin documentos) y opcional detalle */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Servicios
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Totales por clasificación de servicio (servicios continuos y documentos). Haga clic en <strong>Ver detalle</strong> para ver los servicios y documentos que componen cada total.
            </p>

            {(!reportData.servicios || reportData.servicios.length === 0) ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                No hay servicios en el período seleccionado.
              </div>
            ) : (
              <>
                {/* Totales por clasificación: todas las filas (servicios continuos + documentos), agrupadas por service_name */}
                {(() => {
                  const todasLasFilas = reportData.servicios || [];
                  const porClasificacion = {};
                  todasLasFilas.forEach((row) => {
                    const clave = row.service_name && String(row.service_name).trim() ? row.service_name : 'Sin clasificación';
                    if (!porClasificacion[clave]) {
                      porClasificacion[clave] = { service_name: clave, months: {} };
                    }
                    byMonth.forEach((block) => {
                      const val = getDocumentProratedAmount(row, block);
                      porClasificacion[clave].months[block.month_key] = (porClasificacion[clave].months[block.month_key] || 0) + val;
                    });
                  });
                  const totalesRows = Object.values(porClasificacion).sort((a, b) => (a.service_name || '').localeCompare(b.service_name || ''));
                  if (totalesRows.length === 0) {
                    return (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
                        No hay servicios en el período.
                      </div>
                    );
                  }
                  return (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-teal-50">
                            <tr>
                              <th className="px-3 py-3 text-left text-xs font-medium text-teal-800 uppercase">Clasificación del servicio</th>
                              <th className="px-3 py-3 text-left text-xs font-medium text-teal-800 uppercase w-24">Detalle</th>
                              {byMonth.map((block) => (
                                <th key={block.month_key} className="px-3 py-3 text-right text-xs font-medium text-teal-800 uppercase whitespace-nowrap">
                                  {getPeriodLabel(block)} (Monto sin IGV)
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {totalesRows.map((row, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-3 py-3 text-sm font-medium text-gray-900">{row.service_name}</td>
                                <td className="px-3 py-3">
                                  <button
                                    type="button"
                                    onClick={() => openServiciosDetailModal(row.service_name)}
                                    className="text-sm font-medium text-teal-600 hover:text-teal-800 underline"
                                  >
                                    Ver detalle
                                  </button>
                                </td>
                                {byMonth.map((block) => {
                                  const amt = row.months && row.months[block.month_key] != null ? Number(row.months[block.month_key]) : 0;
                                  return (
                                    <td key={block.month_key} className="px-3 py-3 text-sm text-right font-medium">
                                      <span className={amt < 0 ? 'text-red-600' : 'text-gray-900'}>
                                        S/ {amt.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                      </span>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </>
      )}

      {/* Modal: Detalle de salidas (productos de almacén) */}
      {showExitsDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeExitsDetailModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {exitsDetailProduct
                  ? `Detalle de salidas: ${exitsDetailProduct.product_name} — ${exitsDetailProduct.warehouse_name}`
                  : 'Detalle de salidas (productos de almacén)'}
              </h3>
              <button
                type="button"
                onClick={closeExitsDetailModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
              {(exitsDetail?.date_from && exitsDetail?.date_to) || (reportData?.date_from && reportData?.date_to) ? (
                <span>Período: {formatDate(exitsDetail?.date_from || reportData?.date_from)} – {formatDate(exitsDetail?.date_to || reportData?.date_to)}</span>
              ) : null}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {exitsDetailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent" />
                </div>
              ) : exitsDetail?.exits?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay salidas en el período seleccionado.</p>
              ) : exitsDetail?.exits?.length > 0 ? (
                (() => {
                  let periods = reportData?.calcula_productos_by_month || [];
                  const milestonePeriods = reportData?.milestone_periods || [];
                  const useMilestonePeriods = milestonePeriods.length > 0 && periods.some((b) => String(b.month_key || '').startsWith('milestone_'));
                  if (periods.length === 0 && exitsDetail.exits.length > 0 && !useMilestonePeriods) {
                    const monthKeys = [...new Set(exitsDetail.exits.map((row) => row.movement_date && String(row.movement_date).length >= 7 ? String(row.movement_date).substring(0, 7) : null).filter(Boolean))].sort();
                    periods = monthKeys.map((key) => ({ month_key: key }));
                  }
                  const exitsByMonth = {};
                  const assignKey = (movementDate) => {
                    if (!movementDate || String(movementDate).length < 10) return null;
                    const d = String(movementDate).substring(0, 10);
                    if (useMilestonePeriods && milestonePeriods.length > 0) {
                      const found = milestonePeriods.find((p) => d >= p.date_from && d <= p.date_to);
                      return found ? found.key : null;
                    }
                    return String(movementDate).length >= 7 ? String(movementDate).substring(0, 7) : null;
                  };
                  exitsDetail.exits.forEach((row) => {
                    const periodKey = assignKey(row.movement_date);
                    if (periodKey) {
                      if (!exitsByMonth[periodKey]) exitsByMonth[periodKey] = [];
                      exitsByMonth[periodKey].push(row);
                    }
                  });
                  return (
                    <div className="space-y-6">
                      {periods.map((block) => {
                        const monthExits = exitsByMonth[block.month_key] || [];
                        return (
                          <div key={block.month_key} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-teal-50 px-4 py-2 border-b border-gray-200">
                              <h4 className="text-sm font-semibold text-teal-800">{getPeriodLabel(block)}</h4>
                            </div>
                            {monthExits.length === 0 ? (
                              <div className="px-4 py-4 text-sm text-gray-500 text-center">No hay movimientos en este período.</div>
                            ) : (
                              (() => {
                                const totalQty = monthExits.reduce((s, r) => s + Number(r.quantity ?? 0), 0);
                                const totalAmt = monthExits.reduce((s, r) => s + Number(r.total ?? 0), 0);
                                const showVehicleColumn = monthExits.some((r) => {
                                  const label = r?.vehicle_label;
                                  return label != null && String(label).trim() !== '';
                                });
                                return (
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hora</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Guía</th>
                                      {!exitsDetailProduct && (
                                        <>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Almacén</th>
                                        </>
                                      )}
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                                      {showVehicleColumn && (
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vehículo / Maquinaria</th>
                                      )}
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Costo unit.</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto (S/)</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {monthExits.map((row) => (
                                      <tr key={row.id} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-sm text-gray-900">{formatDate(row.movement_date)}</td>
                                        <td className="px-3 py-2 text-sm text-gray-600">{row.movement_time || '—'}</td>
                                        <td className="px-3 py-2 text-sm text-gray-700 font-medium">{row.entry_guide_code || '—'}</td>
                                        {!exitsDetailProduct && (
                                          <>
                                            <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.product_name}</td>
                                            <td className="px-3 py-2 text-sm text-gray-500">{row.product_sku}</td>
                                            <td className="px-3 py-2 text-sm text-gray-600">{row.warehouse_name}</td>
                                          </>
                                        )}
                                        <td className="px-3 py-2 text-sm text-gray-600">{row.tipo_label}</td>
                                        {showVehicleColumn && (
                                          <td className="px-3 py-2 text-sm text-gray-600">{row.vehicle_label || '—'}</td>
                                        )}
                                        <td className={`px-3 py-2 text-sm text-right font-medium ${row.quantity < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                          {row.quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-right text-gray-700">
                                          S/ {(row.unit_price ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                        </td>
                                        <td className={`px-3 py-2 text-sm text-right font-medium ${row.total < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                          S/ {row.total.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                    <tr>
                                      <td colSpan={exitsDetailProduct ? (showVehicleColumn ? 5 : 4) : (showVehicleColumn ? 8 : 7)} className="px-3 py-2 text-sm font-semibold text-gray-800">Total período</td>
                                      <td className="px-3 py-2 text-sm text-right font-semibold text-gray-800">
                                        {totalQty.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-right text-gray-500">—</td>
                                      <td className={`px-3 py-2 text-sm text-right font-semibold ${totalAmt < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                        S/ {totalAmt.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                                );
                              })()
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalle de compra directa */}
      {showComprasDetailModal && comprasDetailProduct && reportData?.compras_sin_almacen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeComprasDetailModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Detalle: {comprasDetailProduct.product_name}
              </h3>
              <button
                type="button"
                onClick={closeComprasDetailModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
              {reportData?.date_from && reportData?.date_to && (
                <span>Período: {formatDate(reportData.date_from)} – {formatDate(reportData.date_to)}. Lista por período solicitado (fecha de ingreso para suministros continuos).</span>
              )}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {(() => {
                const items = reportData.compras_sin_almacen.filter((i) => Number(i.product_id) === Number(comprasDetailProduct.product_id));
                if (items.length === 0) {
                  return <p className="text-center text-gray-500 py-8">No hay ítems de consumo directo para este producto en el período.</p>;
                }
                let periods = reportData?.compras_sin_almacen_by_month || [];
                const milestonePeriods = reportData?.milestone_periods || [];
                const useMilestonePeriods = milestonePeriods.length > 0 && periods.some((b) => String(b.month_key || '').startsWith('milestone_'));
                if (periods.length === 0 && !useMilestonePeriods) {
                  const monthKeys = [...new Set(items.map((i) => i.issue_date && String(i.issue_date).length >= 7 ? String(i.issue_date).substring(0, 7) : null).filter(Boolean))].sort();
                  periods = monthKeys.map((key) => ({ month_key: key }));
                }
                const assignPeriodKey = (dateStr) => {
                  if (!dateStr || String(dateStr).length < 10) return String(dateStr).length >= 7 ? String(dateStr).substring(0, 7) : null;
                  const d = String(dateStr).substring(0, 10);
                  if (useMilestonePeriods && milestonePeriods.length > 0) {
                    const found = milestonePeriods.find((p) => d >= p.date_from && d <= p.date_to);
                    return found ? found.key : null;
                  }
                  return String(dateStr).length >= 7 ? String(dateStr).substring(0, 7) : null;
                };
                const itemsByMonth = {};
                items.forEach((item) => {
                  const periodKey = assignPeriodKey(item.issue_date);
                  if (periodKey) {
                    if (!itemsByMonth[periodKey]) itemsByMonth[periodKey] = [];
                    itemsByMonth[periodKey].push(item);
                  }
                });
                const onlySuministros = items.length > 0 && items.every((i) => i.document_type === 'suministro_continuo');
                const tipoDocLabel = (t) => (t === 'suministro_continuo' ? 'Suministro continuo' : t === 'factura' ? 'Factura' : t === 'boleta' ? 'Boleta' : t === 'recibo_por_honorarios' ? 'Recibo por Honorarios' : t === 'nota_credito' ? 'Nota de crédito' : t === 'nota_debito' ? 'Nota de débito' : t === 'otro' ? 'Otro' : t ? String(t) : '—');
                const refDocLabel = (item) => {
                  const ref = item.reference_document_type || item.document_type;
                  if (!ref) return null;
                  return ref === 'factura' ? 'Factura' : ref === 'boleta' ? 'Boleta' : ref === 'recibo_por_honorarios' ? 'Recibo por Honorarios' : ref === 'nota_credito' ? 'Nota de crédito' : ref === 'nota_debito' ? 'Nota de débito' : ref === 'otro' ? 'Otro' : ref === 'suministro_continuo' ? 'Suministro continuo' : ref;
                };
                return (
                  <div className="space-y-6">
                    {periods.map((block) => {
                      const monthItems = (itemsByMonth[block.month_key] || []).slice().sort((a, b) => {
                        const da = a.issue_date || '';
                        const db = b.issue_date || '';
                        return String(da).localeCompare(String(db));
                      });
                      const showVehicleColumn = monthItems.some((item) => {
                        const label = item?.vehicle_label;
                        return label != null && String(label).trim() !== '';
                      });
                      const totalQty = monthItems.reduce((s, r) => s + Number(r.quantity ?? 0), 0);
                      const totalAmt = monthItems.reduce((s, r) => s + Number(r.subtotal ?? r.total ?? 0), 0);
                      return (
                        <div key={block.month_key} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-teal-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between flex-wrap gap-1">
                            <h4 className="text-sm font-semibold text-teal-800">{getPeriodLabel(block)}</h4>
                            {onlySuministros || monthItems.every((i) => i.document_type === 'suministro_continuo') ? (
                              <span className="text-xs font-medium text-teal-700 bg-teal-100 px-2 py-0.5 rounded">Suministros continuos</span>
                            ) : null}
                          </div>
                          {monthItems.length === 0 ? (
                            <div className="px-4 py-4 text-sm text-gray-500 text-center">No hay movimientos en este período.</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                                    {showVehicleColumn && (
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vehículo / Maquinaria</th>
                                    )}
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Costo unit.</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto sin IGV</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {monthItems.map((item) => (
                                    <tr key={item.document_item_id} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 text-sm text-gray-900">{formatDate(item.issue_date)}</td>
                                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                                        {item.document_code || '—'}
                                        {refDocLabel(item) ? ` (${refDocLabel(item)})` : ''}
                                        {(item.document_series || item.document_number) && (
                                          <span className="block text-gray-500 font-normal mt-0.5">
                                            {item.document_series && item.document_number
                                              ? `Serie ${item.document_series}, Nº ${item.document_number}`
                                              : item.document_series
                                                ? `Serie ${item.document_series}`
                                                : `Nº ${item.document_number}`}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-600">{tipoDocLabel(item.document_type)}</td>
                                      <td className="px-3 py-2 text-sm text-gray-600">{item.supplier_name || '—'}</td>
                                      {showVehicleColumn && (
                                        <td className="px-3 py-2 text-sm text-gray-600">{item.vehicle_label || '—'}</td>
                                      )}
                                      <td className="px-3 py-2 text-sm text-right text-gray-900">
                                        {Number(item.quantity ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-right text-gray-700">
                                        S/ {Number(item.unit_price ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                                        S/ {Number(item.subtotal ?? item.total ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                  <tr>
                                    <td colSpan={showVehicleColumn ? 5 : 4} className="px-3 py-2 text-sm font-semibold text-gray-800">Total período</td>
                                    <td className="px-3 py-2 text-sm text-right font-semibold text-gray-800">
                                      {totalQty.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-right text-gray-500">—</td>
                                    <td className="px-3 py-2 text-sm text-right font-semibold text-gray-800">
                                      S/ {totalAmt.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalle de servicios por clasificación */}
      {showServiciosDetailModal && serviciosDetailClassification != null && reportData?.servicios && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeServiciosDetailModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Detalle: {serviciosDetailClassification}
              </h3>
              <button
                type="button"
                onClick={closeServiciosDetailModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
              {reportData?.date_from && reportData?.date_to && (
                <span>Período: {formatDate(reportData.date_from)} – {formatDate(reportData.date_to)}</span>
              )}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {(() => {
                const detailRows = reportData.servicios.filter(
                  (r) => (r.service_name && String(r.service_name).trim() ? r.service_name : 'Sin clasificación') === serviciosDetailClassification
                );
                if (detailRows.length === 0) {
                  return <p className="text-center text-gray-500 py-8">No hay detalle para esta clasificación.</p>;
                }
                let monthBlocks = reportData?.calcula_productos_by_month || [];
                if (monthBlocks.length === 0 && detailRows[0]?.months) {
                  const keys = Object.keys(detailRows[0].months).sort();
                  monthBlocks = keys.map((k) => ({ month_key: k }));
                }
                return (
                  <div className="space-y-6">
                    {monthBlocks.map((block) => {
                      const monthKey = block.month_key;
                      const rowsInMonth = detailRows.filter((row) => {
                        const amt = getDocumentProratedAmount(row, block);
                        return amt !== 0;
                      });
                      const showVehicleColumn = rowsInMonth.some((row) => {
                        const label = row?.vehicle_label;
                        return label != null && String(label).trim() !== '';
                      });
                      const totalMonth = detailRows.reduce((s, row) => s + getDocumentProratedAmount(row, block), 0);
                      return (
                        <div key={monthKey} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-teal-50 px-4 py-2 border-b border-gray-200">
                            <h4 className="text-sm font-semibold text-teal-800">{getPeriodLabel(monthKey)}</h4>
                          </div>
                          {rowsInMonth.length === 0 ? (
                            <div className="px-4 py-4 text-sm text-gray-500 text-center">No hay movimientos en este período.</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Origen</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Servicio / Descripción</th>
                                    {showVehicleColumn && (
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vehículo / Maquinaria</th>
                                    )}
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto sin IGV</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {rowsInMonth.map((row, idx) => {
                                    const amt = getDocumentProratedAmount(row, block);
                                    return (
                                      <tr key={`${row.source_type}-${row.source_id}-${monthKey}-${idx}`} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-sm text-gray-600">
                                          {row.source_type === 'continuous_service' ? 'Servicio continuo' : 'Documento'}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-600">
                                          {row.source_type === 'continuous_service' ? (row.status === 'open' ? 'Abierto' : 'Cerrado') : '—'}
                                        </td>
                                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                                          {row.source_type === 'continuous_service' ? (
                                            <button
                                              type="button"
                                              onClick={() => openCSDetailModal(row.source_id, monthKey)}
                                              className="text-teal-600 hover:text-teal-800 hover:underline text-left"
                                            >
                                              {row.label}
                                            </button>
                                          ) : (
                                            <>
                                              {row.label}
                                              {(row.document_series || row.document_number) && (
                                                <span className="block text-gray-500 font-normal mt-0.5">
                                                  {row.document_series && row.document_number
                                                    ? `Serie ${row.document_series}, Nº ${row.document_number}`
                                                    : row.document_series
                                                      ? `Serie ${row.document_series}`
                                                      : `Nº ${row.document_number}`}
                                                </span>
                                              )}
                                              {row.service_start_date && row.service_end_date && (
                                                <button
                                                  type="button"
                                                  onClick={() => openProrrateoModal(row)}
                                                  className="mt-1 text-teal-600 hover:text-teal-800 hover:underline text-sm"
                                                >
                                                  Ver fórmula de prorrateo
                                                </button>
                                              )}
                                            </>
                                          )}
                                        </td>
                                        {showVehicleColumn && (
                                          <td className="px-3 py-2 text-sm text-gray-600">
                                            {row.vehicle_label || '—'}
                                          </td>
                                        )}
                                        <td className="px-3 py-2 text-sm text-right">
                                          <span className={amt < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                            S/ {amt.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                  <tr>
                                    <td colSpan={showVehicleColumn ? 4 : 3} className="px-3 py-2 text-sm font-semibold text-gray-800">Total período</td>
                                    <td className={`px-3 py-2 text-sm text-right font-semibold ${totalMonth < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                      S/ {totalMonth.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalle de un servicio continuo (lista de avances) */}
      {showCSDetailModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={closeCSDetailModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {csDetailData ? `Servicio continuo: ${csDetailData.code || `SC-${csDetailId}`}` : 'Servicio continuo'}
              </h3>
              <button
                type="button"
                onClick={closeCSDetailModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {csDetailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent" />
                </div>
              ) : !csDetailData ? (
                <p className="text-center text-gray-500 py-8">No se pudo cargar el servicio.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div><span className="text-gray-500">Proveedor:</span> <span className="font-medium">{csDetailData.supplier?.name || '—'}</span></div>
                    <div><span className="text-gray-500">Estado:</span> <span className="font-medium">{csDetailData.status === 'open' ? 'Abierto' : 'Cerrado'}</span></div>
                    <div><span className="text-gray-500">Fecha inicio:</span> <span className="font-medium">{formatDate(csDetailData.start_date)}</span></div>
                    <div><span className="text-gray-500">Fecha fin:</span> <span className="font-medium">{formatDate(csDetailData.end_date)}</span></div>
                  </div>
                  {csDetailPeriod && (
                    <p className="text-sm text-teal-700 font-medium mb-2">
                      Mostrando solo registros de {getPeriodLabel(csDetailPeriod)}
                    </p>
                  )}
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Registros de avance</h4>
                  {(() => {
                    const allProgress = csDetailData.progress || [];
                    const milestonePeriods = reportData?.milestone_periods || [];
                    const milestone = milestonePeriods.find((p) => p.key === csDetailPeriod);
                    const progressFiltered = csDetailPeriod
                      ? milestone
                        ? allProgress.filter((p) => {
                            const d = p.progress_date ? String(p.progress_date).substring(0, 10) : '';
                            return d >= milestone.date_from && d <= milestone.date_to;
                          })
                        : allProgress.filter((p) => {
                            const d = p.progress_date ? String(p.progress_date).substring(0, 7) : '';
                            return d === csDetailPeriod;
                          })
                      : allProgress;
                    if (progressFiltered.length === 0) {
                      return <p className="text-gray-500 py-4">{csDetailPeriod ? 'No hay registros de avance en este período.' : 'No hay registros de avance.'}</p>;
                    }
                    const sorted = progressFiltered.slice().sort((a, b) => String(a.progress_date || '').localeCompare(String(b.progress_date || '')));
                    const totalSub = sorted.reduce((s, p) => s + Number(p.subtotal ?? (p.quantity * p.unit_price) ?? 0), 0);
                    return (
                      <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Precio unit.</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Subtotal (sin IGV)</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {sorted.map((item) => (
                              <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-sm text-gray-900">{formatDate(item.progress_date)}</td>
                                <td className="px-3 py-2 text-sm text-gray-700">{item.service?.name || '—'}</td>
                                <td className="px-3 py-2 text-sm text-gray-600">{item.description || '—'}</td>
                                <td className="px-3 py-2 text-sm text-right text-gray-900">
                                  {Number(item.quantity ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                </td>
                                <td className="px-3 py-2 text-sm text-right text-gray-700">
                                  S/ {Number(item.unit_price ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                </td>
                                <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                                  S/ {Number(item.subtotal ?? (item.quantity * item.unit_price) ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            <tr>
                              <td colSpan={5} className="px-3 py-2 text-sm font-semibold text-gray-800">Total</td>
                              <td className="px-3 py-2 text-sm text-right font-semibold text-gray-800">
                                S/ {totalSub.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Fórmula de prorrateo (documentos) */}
      {showProrrateoModal && prorrateoDocRow && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50" onClick={closeProrrateoModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Fórmula de prorrateo – {prorrateoDocRow.label || 'Documento'}
              </h3>
              <button
                type="button"
                onClick={closeProrrateoModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              <p className="text-sm text-gray-700">
                El monto total del documento se distribuye proporcionalmente según los días entre la fecha de inicio y fin del servicio.
                Para cada mes del reporte: <strong>(Monto total × Días del documento en el mes) ÷ Total días del documento = Monto del mes.</strong>
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Fecha inicio:</span> <span className="font-medium">{formatDate(prorrateoDocRow.service_start_date)}</span></div>
                <div><span className="text-gray-500">Fecha fin:</span> <span className="font-medium">{formatDate(prorrateoDocRow.service_end_date)}</span></div>
                <div><span className="text-gray-500">Total días del documento:</span> <span className="font-medium">{totalDaysInRange(prorrateoDocRow.service_start_date, prorrateoDocRow.service_end_date)}</span></div>
                <div><span className="text-gray-500">Monto total (sin IGV):</span> <span className="font-medium">S/ {Number(prorrateoDocRow.total_amount ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span></div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Desglose por período</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mes</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Días en el período</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fórmula con números</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto (S/)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        const totalDocDays = totalDaysInRange(prorrateoDocRow.service_start_date, prorrateoDocRow.service_end_date);
                        const totalAmount = Number(prorrateoDocRow.total_amount ?? 0);
                        const fmt = (n) => n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
                        return (reportData?.calcula_productos_by_month || []).map((block) => {
                          const days = block.date_from && block.date_to
                            ? overlapDaysInPeriod(prorrateoDocRow.service_start_date, prorrateoDocRow.service_end_date, block.date_from, block.date_to)
                            : 0;
                          const amt = getDocumentProratedAmount(prorrateoDocRow, block);
                          if (days === 0 && amt === 0) return null;
                          const formula = totalDocDays > 0
                            ? `(S/ ${fmt(totalAmount)} × ${days}) ÷ ${totalDocDays} = S/ ${fmt(amt)}`
                            : '—';
                          return (
                            <tr key={block.month_key}>
                              <td className="px-3 py-2 text-sm text-gray-900">{getPeriodLabel(block)}</td>
                              <td className="px-3 py-2 text-sm text-right text-gray-700">{days}</td>
                              <td className="px-3 py-2 text-sm text-gray-700 font-mono">{formula}</td>
                              <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                                S/ {amt.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Results;
